// frontend/webapp/src/hooks/useCustomHolidays.ts
//
// Fetches and mutates user-created custom holidays from public.custom_holidays.
// Scoped to the authenticated user (RLS enforces ownership).
// Cache: staleTime Infinity — custom holidays only change via user action in
// this session, so explicit mutation-driven invalidation is sufficient.
// The 5-minute staleTime was incorrect per the spec.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CustomHoliday } from '@/types/holiday';

// ── Snake ↔ Camel helpers ────────────────────────────────────────────────────

function toCamel(row: {
  id: string;
  user_id: string;
  client_id: string | null;
  name: string;
  types: string[];
  start_month: number;
  start_day: number;
  end_month: number | null;
  end_day: number | null;
}): CustomHoliday {
  return {
    id: row.id,
    userId: row.user_id,
    clientId: row.client_id,
    name: row.name,
    types: row.types ?? [],
    startMonth: row.start_month,
    startDay: row.start_day,
    endMonth: row.end_month,
    endDay: row.end_day,
  };
}

function toSnake(h: Omit<CustomHoliday, 'id' | 'userId'>) {
  return {
    client_id: h.clientId,
    name: h.name,
    types: h.types,
    start_month: h.startMonth,
    start_day: h.startDay,
    end_month: h.endMonth ?? null,
    end_day: h.endDay ?? null,
  };
}

// ── Query ────────────────────────────────────────────────────────────────────

/**
 * Fetch custom holidays with optional client_id scoping.
 *
 * @param clientId
 *   - `undefined` → all rows owned by the user (personal + every client-scoped)
 *   - `null`      → personal catalog only (client_id IS NULL)
 *   - `string`    → personal catalog + that specific client's scoped rows
 */
async function fetchCustomHolidays(clientId?: string | null): Promise<CustomHoliday[]> {
  let query = supabase
    .from('custom_holidays')
    .select('id, user_id, client_id, name, types, start_month, start_day, end_month, end_day')
    .order('name', { ascending: true });

  if (clientId === null) {
    // Personal catalog only
    query = query.is('client_id', null);
  } else if (clientId !== undefined) {
    // Personal + this client's scoped rows
    query = query.or(`client_id.is.null,client_id.eq.${clientId}`);
  }
  // clientId === undefined → no filter, all rows (RLS handles ownership)

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map(toCamel);
}

export function useCustomHolidays(clientId?: string | null) {
  return useQuery<CustomHoliday[], Error>({
    queryKey: ['custom-holidays', clientId ?? 'user'],
    queryFn: () => fetchCustomHolidays(clientId),
    staleTime: Infinity,
  });
}

// ── Create ────────────────────────────────────────────────────────────────────

export function useCreateCustomHoliday() {
  const queryClient = useQueryClient();

  return useMutation<CustomHoliday, Error, Omit<CustomHoliday, 'id' | 'userId'>>({
    mutationFn: async (input) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('custom_holidays')
        .insert({ ...toSnake(input), user_id: user.id })
        .select('id, user_id, client_id, name, types, start_month, start_day, end_month, end_day')
        .single();

      if (error) throw error;
      return toCamel(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-holidays'] });
    },
  });
}

// ── Update ────────────────────────────────────────────────────────────────────

export function useUpdateCustomHoliday() {
  const queryClient = useQueryClient();

  return useMutation<CustomHoliday, Error, { id: string } & Omit<CustomHoliday, 'id' | 'userId'>>({
    mutationFn: async ({ id, ...input }) => {
      const { data, error } = await supabase
        .from('custom_holidays')
        .update(toSnake(input))
        .eq('id', id)
        .select('id, user_id, client_id, name, types, start_month, start_day, end_month, end_day')
        .single();

      if (error) throw error;
      return toCamel(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-holidays'] });
    },
  });
}

// ── Delete ────────────────────────────────────────────────────────────────────

export function useDeleteCustomHoliday() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('custom_holidays')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-holidays'] });
    },
  });
}
